import React, { useCallback, useEffect, useRef } from "react";
import { verticalListSortingStrategy } from "@dnd-kit/sortable";
import AddPageBlock from "organisms/page-blocks/add-page-block/add-page-block";
import {
    DEFAULT_SORTABLE_DRAG_AND_DROP_SCOPE_NAME,
    SharedSortableDragAndDropList,
    SortableDragAndDropScopeConfiguration,
} from "organisms/sortable-drap-and-drop/shared-sortable-drag-and-drop-list";
import usePageBlocks from "utilities/hooks/domain/notebooks/pages/use-page-blocks";
import { ShowOnlyIfReadonly } from "utilities/contexts/readonly/show-only-if-readonly";
import { HideIfReadonly } from "utilities/contexts/readonly/hide-if-readonly";
import { ReadonlyPageBlock } from "organisms/page-blocks/readonly-page-block/readonly-page-block";
import useOutsideClick from "utilities/hooks/use-outside-click";
import { PageBlock } from "./page-block";
import { NotebookReferencePanelRefAtom } from "utilities/atoms/notebook-reference-panel-ref-atom";
import { useAtomValue } from "jotai";
import useDragAndDropSection from "organisms/drag-and-drop/use-drag-and-drop-section";
import { PageBlockDraggable } from "organisms/page-blocks/page-block-draggable/page-block-draggable";
import { closestCenter, CollisionDetection } from "@dnd-kit/core";
import PageBlockAddMenu from "organisms/page-blocks/page-block-list/page-block-add-menu";
import { IconSizes } from "atoms/constants/icon-sizes";
import { ChecklistItemNoteModalRefAtom } from "organisms/checklist-item-notes/checklist-item-note-modal";
import Loader from "atoms/loaders/loader";
import useKeyPress from "utilities/hooks/use-key-press";
import { BlockRecord } from "models/view-models/block-record";
import LockedForEditingWrapper from "organisms/page-blocks/locked-for-editing/locked-for-editing-wrapper";
import useCurrentIdentity from "utilities/hooks/use-current-identity";
import { usePageEditSessionContext } from "utilities/contexts/page-edit-session/page-edit-session-context-provider";
import useCurrentPageEditSession from "pages/notebooks/use-current-page-edit-session";
import { ToastManager } from "utilities/toast/toast-manager";
import { t } from "utilities/localization-utils";
import useFeatureFlags from "utilities/hooks/use-feature-flags";
import { usePageActivateBlockQueryParams } from "utilities/hooks/domain/notebooks/pages/use-page-activate-block-query-params";
import { CollectionUtils } from "utilities/collection-utils";
import { usePageFetchAllBlocksQueryParams } from "utilities/hooks/domain/notebooks/pages/use-page-fetch-all-blocks-query-params";

interface PageBlockListProps {
    onAnyBlockInListUpdated: () => Promise<void>;
    isInEditMode: boolean;
    onEditSessionTimeout: () => void;
    onRequestEditMode?: () => void;
}

const CSS_CLASS_NAME = "c-page-block-list";
export const PAGE_BLOCKS_DRAG_AND_DROP_SCOPE = `${DEFAULT_SORTABLE_DRAG_AND_DROP_SCOPE_NAME}-page-blocks`;

const PageBlockList = (props: PageBlockListProps) => {
    const {
        onAnyBlockInListUpdated,
        isInEditMode,
        onEditSessionTimeout,
        onRequestEditMode,
    } = props;
    const handleAnyBlockInListUpdated = useCallback(async (): Promise<void> => {
        await onAnyBlockInListUpdated();
    }, [onAnyBlockInListUpdated]);
    const { currentIdentity } = useCurrentIdentity();
    const listRef = useRef<HTMLElement>(null);
    const sentinelRef = useRef<HTMLDivElement | null>(null);

    // Tracks a single in-flight start request so rapid clicks do not create duplicate POSTs.
    const startEditSessionPromiseRef = useRef<Promise<boolean> | null>(null);
    // True after start succeeds and before session state hydrates into currentUserActiveSession.
    const isWaitingForSessionHydrationRef = useRef(false);
    // Holds the latest clicked block while waiting for hydration so we can replay with update.
    const pendingBlockIdRef = useRef<number | null>(null);

    const notebookPanelRef = useAtomValue(NotebookReferencePanelRefAtom);
    const checklistItemNoteModalRef = useAtomValue(
        ChecklistItemNoteModalRefAtom
    );
    const { allowNotebookCollaboration } = useFeatureFlags();
    const { fetchAllBlocks, refreshKey } = usePageFetchAllBlocksQueryParams();

    const {
        activeBlock,
        deleteBlock: onDelete,
        draggingBlockId,
        hasNoBlocks,
        loading,
        moveBlocks: onMove,
        moveBlockToBottom: onMoveDown,
        moveBlockToTop: onMoveUp,
        onActivateBlock: activateBlock,
        onDeactivateBlock: deactivateBlock,
        onDragEnd,
        onDragStart,
        populatedBlocks,
        sortedBlocks,
        startNewBlock,
        startNewBlockAtEnd,
        updateBlockInList,
        fetchingBlocks,
        observerRef,
        refreshBlocksInListById,
    } = usePageBlocks({
        fetchAllBlocks: fetchAllBlocks,
        fetchAllBlocksRefreshKey: refreshKey,
        onAnyBlockInListUpdated: handleAnyBlockInListUpdated,
    });

    const {
        currentUserActiveSession,
        getSessionForBlock,
        startEditSession,
        updateEditSession,
        refreshSessionList,
    } = usePageEditSessionContext({
        handleBlocksChangedByAnotherUser: refreshBlocksInListById,
    });

    useDragAndDropSection();

    useEffect(() => {
        if (currentUserActiveSession?.id) {
            isWaitingForSessionHydrationRef.current = false;
        }
    }, [currentUserActiveSession?.id]);

    useEffect(() => {
        const pendingBlockId = pendingBlockIdRef.current;
        const currentSessionId = currentUserActiveSession?.id;

        if (!pendingBlockId || !currentSessionId) {
            return;
        }

        if (currentUserActiveSession.blockId === pendingBlockId) {
            pendingBlockIdRef.current = null;
            return;
        }

        pendingBlockIdRef.current = null;
        updateEditSession(currentSessionId, pendingBlockId);
    }, [currentUserActiveSession, updateEditSession]);

    const handleEditSessionUpdate = useCallback(
        async (blockId?: number): Promise<boolean> => {
            if (!isInEditMode) {
                return false;
            }

            if (currentUserActiveSession?.id) {
                return await updateEditSession(
                    currentUserActiveSession.id,
                    blockId
                );
            }

            if (isWaitingForSessionHydrationRef.current) {
                pendingBlockIdRef.current = blockId ?? null;
                refreshSessionList();
                return true;
            }

            if (!startEditSessionPromiseRef.current) {
                startEditSessionPromiseRef.current = startEditSession(blockId!)
                    .then((canStart) => {
                        if (canStart) {
                            isWaitingForSessionHydrationRef.current = true;
                        }

                        return canStart;
                    })
                    .finally(() => {
                        startEditSessionPromiseRef.current = null;
                    });
            }

            return await startEditSessionPromiseRef.current;
        },
        [
            currentUserActiveSession,
            isInEditMode,
            refreshSessionList,
            startEditSession,
            updateEditSession,
        ]
    );

    const handleBlockSaved = useCallback(
        (updatedBlock: BlockRecord): void => {
            updateBlockInList(updatedBlock);
            handleAnyBlockInListUpdated();
            handleEditSessionUpdate(updatedBlock.id);
        },
        [
            updateBlockInList,
            handleAnyBlockInListUpdated,
            handleEditSessionUpdate,
        ]
    );

    const handleActivateBlock = useCallback(
        async (block: BlockRecord) => {
            if (!allowNotebookCollaboration) {
                activateBlock(block);
                return;
            }
            try {
                const canEdit = await handleEditSessionUpdate(block.id);
                if (canEdit) {
                    activateBlock(block);
                } else {
                    refreshSessionList();
                }
            } catch (error) {
                ToastManager.error(
                    t("errors-actionResource", {
                        action: t("updating"),
                        resource: t("block"),
                    })
                );
                console.error(error);
            }
        },
        [
            activateBlock,
            allowNotebookCollaboration,
            handleEditSessionUpdate,
            refreshSessionList,
        ]
    );

    const handleDeactivateBlock = async (blockId?: number) => {
        if (!blockId) return;
        const sessionId = getSessionForBlock(blockId)?.id;
        if (sessionId) {
            await updateEditSession(sessionId);
        }
        deactivateBlock();
    };

    const keepOpenOnClickElements = [
        listRef,
        ...(notebookPanelRef ? [notebookPanelRef] : []),
        ...(checklistItemNoteModalRef ? [checklistItemNoteModalRef] : []),
    ];
    useOutsideClick(
        keepOpenOnClickElements,
        handleDeactivateBlock,
        "mousedown"
    );

    useCurrentPageEditSession({
        isInActiveEditSession: currentUserActiveSession != null,
        isInEditMode,
        onSessionEnd: onEditSessionTimeout,
    });

    const handleActivateBlockChange = useCallback(
        async (blockId: number) => {
            const blockEditSession = getSessionForBlock(blockId);
            const isLockedForEditing =
                blockEditSession &&
                blockEditSession?.userId !== currentIdentity?.userId() &&
                blockEditSession?.blockId === blockId;
            if (!isLockedForEditing) {
                const blockToActivate = sortedBlocks.find(
                    (block) => block.id === blockId
                );
                if (blockToActivate) {
                    await handleActivateBlock(blockToActivate);
                }
            }
        },
        [currentIdentity, getSessionForBlock, handleActivateBlock, sortedBlocks]
    );

    usePageActivateBlockQueryParams({
        areBlocksLoaded:
            CollectionUtils.hasValues(sortedBlocks) &&
            !loading &&
            !fetchingBlocks,
        isInEditMode,
        onActiveBlockChange: handleActivateBlockChange,
    });

    useKeyPress("Escape", handleDeactivateBlock);

    const isFirst = (index: number): boolean => index === 0;
    const isLast = (index: number): boolean =>
        index === sortedBlocks.length - 1;

    return (
        <section className={CSS_CLASS_NAME} ref={listRef}>
            {loading ? (
                <div className={`${CSS_CLASS_NAME}__loaderWrapper`}>
                    <Loader />
                </div>
            ) : (
                <>
                    <ShowOnlyIfReadonly>
                        {populatedBlocks.map((block) => (
                            <ReadonlyPageBlock
                                block={block}
                                key={block.id}
                                onRequestEditMode={onRequestEditMode}
                            />
                        ))}
                    </ShowOnlyIfReadonly>
                    <HideIfReadonly>
                        <SharedSortableDragAndDropList
                            dragAndDropScopeName={
                                PAGE_BLOCKS_DRAG_AND_DROP_SCOPE
                            }
                            dragAndDropScopeConfiguration={
                                dragAndDropScopeConfiguration
                            }
                            items={sortedBlocks}
                            onDragEnd={onDragEnd}
                            onSortChange={onMove}
                            onDragStart={onDragStart}
                            strategy={verticalListSortingStrategy}>
                            {sortedBlocks.map((block, index) => {
                                const blockEditSession = getSessionForBlock(
                                    block.id!
                                );
                                return (
                                    <LockedForEditingWrapper
                                        key={block.id ?? index}
                                        isLocked={
                                            isInEditMode &&
                                            blockEditSession?.userId !==
                                                currentIdentity?.userId() &&
                                            blockEditSession?.blockId ===
                                                block.id
                                        }
                                        firstName={
                                            blockEditSession?.user?.firstName
                                        }
                                        lastName={
                                            blockEditSession?.user?.lastName
                                        }>
                                        <PageBlock
                                            activateBlock={handleActivateBlock}
                                            activeBlock={activeBlock}
                                            block={block}
                                            draggingBlockId={draggingBlockId}
                                            isFirst={isFirst(index)}
                                            isLast={isLast(index)}
                                            onBlockModified={updateBlockInList}
                                            onBlockSaved={handleBlockSaved}
                                            onDelete={onDelete}
                                            onMoveDown={onMoveDown}
                                            onMoveUp={onMoveUp}
                                            startNewBlock={startNewBlock}
                                            onDeactivateBlock={() =>
                                                handleDeactivateBlock(block.id)
                                            }
                                        />
                                    </LockedForEditingWrapper>
                                );
                            })}
                        </SharedSortableDragAndDropList>
                        {hasNoBlocks && !activeBlock && (
                            <div className={`${CSS_CLASS_NAME}__addContent`}>
                                <AddPageBlock onAddContent={startNewBlock} />
                            </div>
                        )}
                        <div
                            ref={(node) => {
                                sentinelRef.current = node;
                                observerRef(node);
                            }}
                            className={`${CSS_CLASS_NAME}__sentinel`}
                        />
                        {fetchingBlocks && (
                            <div className={`${CSS_CLASS_NAME}__bottomLoader`}>
                                <Loader />
                            </div>
                        )}
                        <PageBlockAddMenu
                            showMenuOnLeft
                            size={IconSizes.Large}
                            cssClassName={`${CSS_CLASS_NAME}__addButton`}
                            onAddBlockBelow={startNewBlockAtEnd}
                        />
                    </HideIfReadonly>
                </>
            )}
        </section>
    );
};

export default PageBlockList;

const pageBlockCollisionDetection: CollisionDetection = (args) => {
    const topOffset = 10;
    const pointerCenteredCollisionRect = {
        ...args.collisionRect,
        left: args.pointerCoordinates?.x ?? args.collisionRect.left,
        top: (args.pointerCoordinates?.y ?? args.collisionRect.top) + topOffset,
    };

    return closestCenter({
        ...args,
        collisionRect: pointerCenteredCollisionRect,
    });
};

const dragAndDropScopeConfiguration: SortableDragAndDropScopeConfiguration = {
    collisionDetection: pageBlockCollisionDetection,
    contextModifiers: [],
    draggableComponent: PageBlockDraggable,
};
